/**
 * Shrink a picked image to something a client logo can reasonably be, and hand
 * it back as a PNG data URL.
 *
 * Doing this in the browser rather than on the server is what lets the logo
 * travel as JSON and live in its row: whatever gets picked — a 4MB photograph
 * of a business card, a 12,000px export — leaves here as a few kilobytes, and
 * the normalisation to PNG means the API only ever stores one format it asked
 * for.
 */
export const LOGO_MAX_EDGE = 256;

export async function shrinkImage(file: File, maxEdge = LOGO_MAX_EDGE): Promise<string> {
  const bitmap = await createImageBitmap(file);
  try {
    const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    if (!context) throw new Error("no 2d context");
    // A logo is usually on transparent or white; keeping the alpha channel means
    // one on transparent does not pick up a black box behind it.
    context.drawImage(bitmap, 0, 0, width, height);
    return canvas.toDataURL("image/png");
  } finally {
    bitmap.close();
  }
}
